import {BaseButton, BaseInput, BaseModal, BaseSelect} from "@/components";
import {useCreateProviderMethod} from "@/hooks/useProviderMethods";
import {useProviders} from "@/hooks/useProviders";
import type {CreateProviderMethodDto, OperationType, ProviderCategory} from "@/types/provider-method.types";
import {useState} from "react";

interface CreateProviderMethodModalProps {
  onClose: () => void;
}

const countryOptions = [
  {value: "CO", label: "Colombia"},
  {value: "CL", label: "Chile"},
  {value: "PE", label: "Perú"},
  {value: "MX", label: "México"},
  {value: "AR", label: "Argentina"},
  {value: "GLOBAL", label: "Global"},
];

const countryNameMap: Record<string, string> = {
  CO: "Colombia",
  CL: "Chile",
  PE: "Perú",
  MX: "México",
  AR: "Argentina",
  GLOBAL: "Global",
};

const operationOptions = [
  {value: "deposit", label: "Depósito"},
  {value: "withdrawal", label: "Retiro"},
];

const currencyOptions = [
  {value: "COP", label: "COP"},
  {value: "CLP", label: "CLP"},
  {value: "PEN", label: "PEN"},
  {value: "MXN", label: "MXN"},
  {value: "ARS", label: "ARS"},
  {value: "USD", label: "USD"},
  {value: "USDT", label: "USDT"},
];

const categoryOptions: {value: ProviderCategory; label: string}[] = [
  {value: "bank_transfer", label: "Transferencia bancaria"},
  {value: "crypto", label: "Crypto"},
  {value: "card", label: "Tarjeta"},
  {value: "cash", label: "Efectivo"},
  {value: "ewallet", label: "E-Wallet"},
];

const initialForm = {
  payment_provider_id: "",
  country_code: "CO",
  currency: "COP",
  operation_type: "deposit" as OperationType,
  category: "bank_transfer" as ProviderCategory,
  min_amount_cents: "0",
  max_amount_cents: "0",
  commission_percentage: "0",
  commission_fixed_cents: "0",
  processing_time: "Instant",
  display_order: "1",
  requires_email: false,
  requires_phone: false,
  requires_bank_account: false,
  requires_beneficiary_name: false,
  requires_beneficiary_doc: false,
  requires_wallet_address: false,
  requires_network: false,
};

export const CreateProviderMethodModal = ({onClose}: CreateProviderMethodModalProps) => {
  const createMutation = useCreateProviderMethod();
  const {data: providers} = useProviders();
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");

  const providerOptions = (providers ?? []).map(p => ({value: p.id, label: `${p.name} (${p.code})`}));

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");

    if (!form.payment_provider_id) {
      setError("Selecciona un proveedor");
      return;
    }

    const body: CreateProviderMethodDto = {
      payment_provider_id: form.payment_provider_id,
      country_code: form.country_code,
      country_name: countryNameMap[form.country_code] ?? form.country_code,
      currency: form.currency,
      operation_type: form.operation_type,
      category: form.category,
      min_amount_cents: Number(form.min_amount_cents),
      max_amount_cents: Number(form.max_amount_cents),
      commission_percentage: Number(form.commission_percentage),
      commission_fixed_cents: Number(form.commission_fixed_cents),
      processing_time: form.processing_time,
      icon: "",
      requires_email: form.requires_email,
      requires_phone: form.requires_phone,
      requires_bank_account: form.requires_bank_account,
      requires_beneficiary_name: form.requires_beneficiary_name,
      requires_beneficiary_doc: form.requires_beneficiary_doc,
      requires_wallet_address: form.requires_wallet_address,
      requires_network: form.requires_network,
      is_active: false,
      display_order: Number(form.display_order),
      metadata: {},
    };

    try {
      await createMutation.mutateAsync(body);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al crear el método de pago");
    }
  };

  const set = (key: string, value: string | boolean) => setForm(prev => ({...prev, [key]: value}));

  return (
    <BaseModal
      open
      onClose={onClose}
      title="Nuevo Método de Pago por País"
      size="xl"
      footer={
        <>
          <BaseButton
            variant="ghost"
            size="md"
            onClick={onClose}
          >
            Cancelar
          </BaseButton>
          <BaseButton
            variant="primary"
            size="md"
            loading={createMutation.isPending}
            onClick={handleSubmit}
          >
            Crear
          </BaseButton>
        </>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
      >
        <BaseSelect
          label="Proveedor"
          options={[{value: "", label: "Seleccionar proveedor..."}, ...providerOptions]}
          value={form.payment_provider_id}
          onChange={e => set("payment_provider_id", e.target.value)}
        />

        <div className="grid grid-cols-3 gap-3">
          <BaseSelect
            label="País"
            options={countryOptions}
            value={form.country_code}
            onChange={e => set("country_code", e.target.value)}
          />
          <BaseSelect
            label="Moneda"
            options={currencyOptions}
            value={form.currency}
            onChange={e => set("currency", e.target.value)}
          />
          <BaseSelect
            label="Operación"
            options={operationOptions}
            value={form.operation_type}
            onChange={e => set("operation_type", e.target.value)}
          />
        </div>

        <BaseSelect
          label="Categoría"
          options={categoryOptions}
          value={form.category}
          onChange={e => set("category", e.target.value)}
        />

        <div className="grid grid-cols-2 gap-3">
          <BaseInput
            label="Tiempo procesamiento"
            placeholder="Instant"
            value={form.processing_time}
            onChange={e => set("processing_time", e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <BaseInput
            label="Monto mínimo (centavos)"
            type="number"
            value={form.min_amount_cents}
            onChange={e => set("min_amount_cents", e.target.value)}
          />
          <BaseInput
            label="Monto máximo (centavos)"
            type="number"
            value={form.max_amount_cents}
            onChange={e => set("max_amount_cents", e.target.value)}
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <BaseInput
            label="Comisión %"
            type="number"
            step="0.01"
            value={form.commission_percentage}
            onChange={e => set("commission_percentage", e.target.value)}
          />
          <BaseInput
            label="Comisión fija (centavos)"
            type="number"
            value={form.commission_fixed_cents}
            onChange={e => set("commission_fixed_cents", e.target.value)}
          />
          <BaseInput
            label="Orden"
            type="number"
            value={form.display_order}
            onChange={e => set("display_order", e.target.value)}
          />
        </div>

        {/* Requires checkboxes */}
        <div>
          <p className="text-sm font-medium text-(--color-text) mb-2">Campos requeridos al usuario</p>
          <div className="grid grid-cols-2 gap-2">
            {(
              [
                ["requires_email", "Email"],
                ["requires_phone", "Teléfono"],
                ["requires_bank_account", "Cuenta bancaria"],
                ["requires_beneficiary_name", "Nombre beneficiario"],
                ["requires_beneficiary_doc", "Documento beneficiario"],
                ["requires_wallet_address", "Dirección wallet"],
                ["requires_network", "Red blockchain"],
              ] as const
            ).map(([key, label]) => (
              <label
                key={key}
                className="flex items-center gap-2 text-sm text-(--color-text-secondary)"
              >
                <input
                  type="checkbox"
                  checked={form[key]}
                  onChange={e => set(key, e.target.checked)}
                  className="rounded border-(--color-border)"
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-(--color-danger)">{error}</p>}
      </form>
    </BaseModal>
  );
};
